/**
 * Subscription Service
 * API client for billing/subscription endpoints
 */

import { api } from './api';
import type {
  SubscriptionStatusResponse,
  UserLimits,
  FeatureCheckResponse,
  UpdateLocaleRequest,
  UpdateLocaleResponse,
  RetryPaymentRequest,
  RetryPaymentResponse,
  PublicPlan,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  ChangePlanRequest,
  CancelSubscriptionRequest,
  PaymentsResponse,
  FullSubscription,
  SubscriptionHistoryItem,
} from '@/types/subscription';

export const subscriptionService = {
  // ============ STATUS & LIMITS ============

  /**
   * Get current subscription status with alerts
   * GET /subscriptions/status
   */
  getStatus: () => api.get<SubscriptionStatusResponse>('/subscriptions/status'),

  /**
   * Get user's current limits and usage
   * GET /billing/limits
   */
  getLimits: () => api.get<UserLimits>('/billing/limits'),

  /**
   * Check if user has access to a specific feature
   * GET /billing/features/:feature
   */
  checkFeature: (feature: string) =>
    api.get<FeatureCheckResponse>(`/billing/features/${feature}`),

  // ============ PLANS ============

  /**
   * List all available plans
   * GET /plans
   */
  listPlans: () => api.get<PublicPlan[]>('/plans'),

  /**
   * Get plan details by ID
   * GET /plans/:id
   */
  getPlan: (planId: string) => api.get<PublicPlan>(`/plans/${planId}`),

  // ============ SUBSCRIPTIONS ============

  /**
   * Get current subscription with full details
   * GET /subscriptions/current
   */
  getCurrentSubscription: () => api.get<FullSubscription>('/subscriptions/current'),

  /**
   * List subscription history
   * GET /subscriptions
   */
  listSubscriptions: () => api.get<SubscriptionHistoryItem[]>('/subscriptions'),

  /**
   * Create a new subscription (upgrade to paid plan)
   * POST /subscriptions
   * Returns initPoint URL for Mercado Pago checkout
   */
  createSubscription: (data: CreateSubscriptionRequest) =>
    api.post<CreateSubscriptionResponse>('/subscriptions', data),

  /**
   * Change subscription plan (upgrade/downgrade)
   * PATCH /subscriptions/:id/change-plan
   */
  changePlan: (subscriptionId: string, data: ChangePlanRequest) =>
    api.patch<FullSubscription>(`/subscriptions/${subscriptionId}/change-plan`, data),

  /**
   * Pause subscription
   * POST /subscriptions/:id/pause
   */
  pauseSubscription: (subscriptionId: string) =>
    api.post<{ message: string }>(`/subscriptions/${subscriptionId}/pause`),

  /**
   * Resume paused subscription
   * POST /subscriptions/:id/resume
   */
  resumeSubscription: (subscriptionId: string) =>
    api.post<{ message: string }>(`/subscriptions/${subscriptionId}/resume`),

  /**
   * Cancel subscription
   * DELETE /subscriptions/:id
   */
  cancelSubscription: (subscriptionId: string, data?: CancelSubscriptionRequest) =>
    api.delete<{ message: string; cancelledAt: string }>(`/subscriptions/${subscriptionId}`, data),

  // ============ PAYMENTS ============

  /**
   * List payment history
   * GET /payments
   */
  getPayments: (page = 1, limit = 20) =>
    api.get<PaymentsResponse>(`/payments?page=${page}&limit=${limit}`),

  /**
   * Retry a failed payment
   * POST /payments/retry
   */
  retryPayment: (data: RetryPaymentRequest) =>
    api.post<RetryPaymentResponse>('/payments/retry', data),

  // ============ USER ============

  /**
   * Update user locale for emails and communications
   * PATCH /users/me/locale
   */
  updateLocale: (data: UpdateLocaleRequest) =>
    api.patch<UpdateLocaleResponse>('/users/me/locale', data),
};
